import { Model } from './Model'
import type { Group as ServiceGroup } from './api/Group'
import type { Variable } from './Variable'

export enum GroupID {
  ID_ACTUAL = 'NOW',
  ID_2DAYS = '2D',
  ID_10DAYS = '10D',
  ID_30DAYS = '30D',
  ID_7DOBS = '7DOBS',
  ID_30DOBS = '30DOBS',
  ID_FWI = 'FWI',
  ID_FIRMS = 'FIRMS',
  ID_EDO = 'EDO',
  ID_NDVI = 'NDVI',
  ID_CLMS = 'CLMS'
}

export class Group {
  id: GroupID
  icon: string
  name: string
  label: string
  models: Model[]
  isRadiosondeoActive: boolean
  lastModelSelected?: Model
  disabledInReducedMode?: boolean

  constructor(serviceGroup: ServiceGroup) {
    this.id = (serviceGroup.ID ?? serviceGroup.NAME) as GroupID
    this.icon = serviceGroup.ICON
    this.name = serviceGroup.NAME
    this.label = serviceGroup.NAME
    this.disabledInReducedMode = serviceGroup.IS_DISABLED_IN_REDUCED_MODE
    this.isRadiosondeoActive = serviceGroup.ID === GroupID.ID_2DAYS
    this.models = serviceGroup.MODELS?.map(
      (serviceModel) =>
        new Model(
          serviceModel,
          this.isNoLevelsAllowed(),
          this.isNoDateAllowed(),
          this.id,
          this.isBackInTime()
        )
    )
    if (this.models) {
      this.lastModelSelected = this.models[0]
    }

    this.fixEumesatFirst()
  }

  // static GFS_EUROPE = 'GFS_025_EUROPA'

  isNoLevelsAllowed() {
    switch (this.id) {
      case GroupID.ID_ACTUAL:
        return true
      default:
        return false
    }
  }

  isNoDateAllowed() {
    switch (this.id) {
      case GroupID.ID_ACTUAL:
        return true
      default:
        return false
    }
  }

  isActual() {
    return this.id === GroupID.ID_ACTUAL
  }

  is2Days() {
    return this.id === GroupID.ID_2DAYS
  }

  is10Days() {
    return this.id === GroupID.ID_10DAYS
  }

  is30Days() {
    return this.id === GroupID.ID_30DAYS
  }

  isObservaciones7Dias() {
    return this.id === GroupID.ID_7DOBS
  }

  isObservaciones30Dias() {
    return this.id === GroupID.ID_30DOBS
  }

  isObservaciones() {
    return this.isObservaciones7Dias() || this.isObservaciones30Dias()
  }

  isBackInTime() {
    return this.isObservaciones() || this.isActual()
  }

  fixEumesatFirst() {
    if (this.isActual()) {
      // TODO: poner eumetsat primero, hacer en backend
      this.models[0].variables?.sort((a: Variable, b: Variable) => {
        return a.name.localeCompare(b.name)
      })
    }
  }

  hasSomeModelInPersonalizedGroupSelected() {
    return this.models?.some((model) =>
      model.hasSomeVariableInPersonalizedGroupSelected()
    )
  }

  hasModel(modelId: Model['id']): boolean {
    return this.models?.some((model) => model.id === modelId) ?? false
  }
}
